import { Trans, useTranslation } from "react-i18next";
import { Alert, CircularProgress, Stack } from "@mui/material";
import { describeError, useToast } from "../components/toast";
import { useCallback, useEffect, useState } from "react";
import OrderedSelection from "../components/OrderedSelection";
import { foyerApi, type ChannelRow } from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { friendlyError } from "../utils/apiError";
import { PermissionGate } from "../auth/access";
import { AUTHOR_CHANNELS } from "../components/distribution/catalogueGates";

/**
 * Nenda's official channel line-up — Distribution → Line-up.
 *
 * ## What this is, and what it is NOT
 *
 * It is an **ORDER**. Naming a channel here grants no hotel the right to show
 * it and puts it on no rail — entitlement decides that, as it always has. This
 * answers the different question of what POSITION a channel takes once it is
 * shown, which nothing answered before: a channel rail resolves from
 * entitlement, entitlement is a set, and the catalogue answered in whatever
 * order it liked. A Swedish hotel was looking at `SVT24, SVT2, TV4, SVT1`.
 *
 * ## Why it is authored once, globally
 *
 * The Swedish line-up is a **national convention, not a hotel's preference**.
 * SVT1 is channel 1 in every hotel in the country. If each customer had to drag
 * it into place, every one of them would redo the same work, they would drift
 * apart, and a channel ingested next month would land nowhere in particular.
 * Authored here, every premise inherits it and a new channel takes its place by
 * itself.
 *
 * A hotel that wants otherwise says so in its own `Premise.channelOrder`, which
 * outranks this list — see `@shared/channel-order`. That is not an edge case:
 * a hotel's own content channel **cannot** be on a national list by definition,
 * so it always arrives unranked at the tail and the operator moves it up.
 *
 * ## Arrows, not drag
 *
 * `OrderedSelection`'s reasoning applies unchanged: the same operation works
 * with a keyboard, a screen reader and a touch screen, it needs no library, and
 * ordering a national line-up is a rare deliberate act rather than something
 * worth optimising the mouse path for. Each button says where the row is going,
 * so the action is announced rather than felt.
 *
 * ## Not every channel belongs here
 *
 * The list is a curated prefix, not the catalogue. Anything it does not name
 * sorts after everything it does, alphabetically — which is the right place for
 * a channel nobody has decided about, and the only correct place for a hotel's
 * own channel before that hotel moves it.
 */
export default function ChannelLineupPage() {
  const [order, setOrder] = useState<string[] | null>(null);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["channels", "common"]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [saved, list] = await Promise.all([
        foyerApi.channelOrder(),
        // One page, generous: this is a picker over a catalogue an operator
        // reads by eye, and the list route is the same one the Channels page
        // already uses.
        foyerApi.listDistributionChannels({ pageSize: 500 }),
      ]);
      setOrder(saved.channelOrder);
      setChannels(list.channels);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Optimistic, then reconciled from the server's answer.
   *
   * The server de-duplicates on write (a repeated id is a drag that landed
   * twice), so its response is authoritative in a way the local array is not.
   */
  async function save(next: string[]) {
    setOrder(next);
    setError(null);
    try {
      const res = await foyerApi.saveChannelOrder(next);
      setOrder(res.channelOrder);
      toast.success(
        t("channelLineupPage.saved", { count: res.channelOrder.length })
      );
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(describeError(t, "saveLineup", e));
      void load();
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("channelLineupPage.title")}
        subtitle={t("channelLineupPage.subtitle")}
      />

      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={22} />
        </Stack>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && order && (
        <>
          {/* Said out loud because the two are genuinely easy to confuse, and
              confusing them is expensive in both directions: an operator who
              thinks this sells a channel, or who thinks removing a channel here
              takes it off a hotel's televisions. */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Trans
              t={t}
              i18nKey="channelLineupPage.orderNotSelection"
              components={{ strong: <strong /> }}
            />
          </Alert>

          <PermissionGate {...AUTHOR_CHANNELS}>
            <OrderedSelection
              label={t("channelLineupPage.orderLabel")}
              hint={t("channelLineupPage.orderHint")}
              value={order}
              options={channels.map((c) => ({
                id: c.assetId,
                label: c.originalTitle || c.assetId,
              }))}
              onChange={(next) => void save(next)}
              testId="official-channel-order"
            />
          </PermissionGate>
        </>
      )}
    </PageContainer>
  );
}
